import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from '@ui';
import TaskProgressWidget from '../../packages/ui/components/organisms/TaskProgressWidget';
import { Pause, Play, StopCircle, Check } from 'lucide-react';
import { getWhatsAppMessageLogDetailApi, sendWhatsAppMessageApi } from '../services/api';
import {
  buildOrganicBroadcastQueue,
  getOrganicInterval,
} from '../utils/broadcastQueue';

const BroadcastQueueContext = createContext(null);

// The gateway accepting a message is not delivery: queued items are confirmed
// against their message log until the gateway reports sent or failed.
const CONFIRM_POLL_MS = 5000;
const CONFIRM_BATCH_SIZE = 5;
const CONFIRM_TIMEOUT_MS = 10 * 60 * 1000;
const SENT_LOG_STATUSES = ['sent', 'delivered', 'read'];

/**
 * Helper: Sleep with real-time cancel and pause check.
 */
function waitWithControl(ms, cancelRef, pauseRef, onTick) {
  return new Promise((resolve) => {
    const start = Date.now();
    const interval = setInterval(() => {
      if (cancelRef.current) {
        clearInterval(interval);
        resolve(false);
        return;
      }

      if (pauseRef.current) {
        // Paused: do not advance elapsed time
        return;
      }

      const elapsed = Date.now() - start;
      const remainingMs = Math.max(0, ms - elapsed);
      onTick?.(Math.ceil(remainingMs / 1000));

      if (elapsed >= ms) {
        clearInterval(interval);
        resolve(true);
      }
    }, 200);
  });
}

/**
 * BroadcastQueueProvider — Global state and persistent background runner
 * for WhatsApp broadcast sending and multi-product dispatch.
 *
 * Runs completely independent of route navigation and modal states.
 * Renders the persistent <TaskProgressWidget /> (Google Drive-style) via Portal.
 */
export function BroadcastQueueProvider({ children }) {
  const [isQueueActive, setIsQueueActive] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [status, setStatus] = useState('running'); // 'running' | 'paused' | 'completed' | 'cancelled'

  const [queueItems, setQueueItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [countdown, setCountdown] = useState(0);

  // Counts follow item states, so a message only counts as sent once the gateway confirms it
  const successCount = queueItems.filter((it) => it.status === 'success').length;
  const failedCount = queueItems.filter((it) => it.status === 'error' && !it.cancelled).length;
  const queuedCount = queueItems.filter((it) => it.status === 'queued').length;

  // Real-time loop control refs
  const cancelRef = useRef(false);
  const pauseRef = useRef(false);
  pauseRef.current = isPaused;

  // Confirm queued items against their message logs, a few per tick, least recently checked first.
  // A steady interval reading the latest items from a ref keeps sends from postponing each check.
  const itemsRef = useRef(queueItems);
  itemsRef.current = queueItems;
  const hasQueued = queuedCount > 0;

  useEffect(() => {
    if (!hasQueued) return undefined;
    let inFlight = false;

    const timer = setInterval(async () => {
      if (inFlight) return;
      const waiting = itemsRef.current
        .filter((it) => it.status === 'queued' && it.logId && Date.now() - it.queuedAt < CONFIRM_TIMEOUT_MS)
        .sort((a, b) => (a.checkedAt || 0) - (b.checkedAt || 0))
        .slice(0, CONFIRM_BATCH_SIZE);
      if (waiting.length === 0) return;

      inFlight = true;
      const results = await Promise.all(
        waiting.map((it) =>
          getWhatsAppMessageLogDetailApi(it.logId)
            .then((log) => [it.id, log])
            .catch(() => [it.id, null])
        )
      );
      inFlight = false;

      const byId = new Map(results);
      const checkedAt = Date.now();
      setQueueItems((prev) =>
        prev.map((it) => {
          if (!byId.has(it.id) || it.status !== 'queued') return it;
          const log = byId.get(it.id);
          if (SENT_LOG_STATUSES.includes(log?.status)) {
            return { ...it, status: 'success', checkedAt, timestamp: new Date().toLocaleTimeString() };
          }
          if (log?.status === 'failed') {
            return {
              ...it,
              status: 'error',
              checkedAt,
              error: log.error_reason || 'Gagal dikirim oleh WhatsApp Gateway',
              timestamp: new Date().toLocaleTimeString(),
            };
          }
          return { ...it, checkedAt };
        })
      );
    }, CONFIRM_POLL_MS);

    return () => clearInterval(timer);
  }, [hasQueued]);

  const pauseQueue = useCallback(() => {
    setIsPaused(true);
    setStatus('paused');
  }, []);

  const resumeQueue = useCallback(() => {
    setIsPaused(false);
    setStatus('running');
  }, []);

  const cancelQueue = useCallback(() => {
    cancelRef.current = true;
    setIsPaused(false);
    setStatus('cancelled');
    setIsFinished(true);

    // Mark remaining pending items as cancelled; messages already queued at the gateway are left to confirm
    setQueueItems((prev) =>
      prev.map((it) =>
        it.status === 'pending' || it.status === 'active'
          ? { ...it, status: 'error', cancelled: true, error: 'Dibatalkan oleh pengguna' }
          : it
      )
    );
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const dismissQueue = useCallback(() => {
    cancelRef.current = true;
    setIsQueueActive(false);
    setIsMinimized(false);
    setIsPaused(false);
    setIsFinished(false);
    setQueueItems([]);
    setCurrentIndex(0);
    setCountdown(0);
    setStatus('running');
  }, []);

  /**
   * Start a new broadcast queue.
   * Compiles the matrix of recipients and products into individual task items,
   * kicks off the background async runner, and displays the TaskProgressWidget.
   */
  const startQueue = useCallback(async ({ products = [], recipients = [], intervalMs = 5000, skip }) => {
    if (!products.length || !recipients.length) return;

    // Reset loop control flags
    cancelRef.current = false;
    pauseRef.current = false;

    // Build organic Cartesian product queue with shuffled recipients,
    // independent product shuffling per recipient, and randomized copy tone
    const items = buildOrganicBroadcastQueue({ products, recipients, skip });
    if (!items.length) return;

    setQueueItems(items);
    setCurrentIndex(0);
    setCountdown(0);
    setIsPaused(false);
    setIsFinished(false);
    setStatus('running');
    setIsMinimized(false);
    setIsQueueActive(true);

    // Run queue in background asynchronously
    (async () => {
      for (let i = 0; i < items.length; i++) {
        if (cancelRef.current) break;

        // Check if paused
        while (pauseRef.current && !cancelRef.current) {
          await new Promise((r) => setTimeout(r, 250));
        }
        if (cancelRef.current) break;

        const currentTask = items[i];
        setCurrentIndex(i + 1);

        // Mark item as active
        setQueueItems((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, status: 'active' } : it))
        );

        try {
          const res = await sendWhatsAppMessageApi({
            recipient: currentTask.recipient.destination,
            message: currentTask.messageText,
            ...(currentTask.mediaUrl ? { media_url: currentTask.mediaUrl } : {}),
            ...(currentTask.product?.item_id != null ? { item_id: currentTask.product.item_id } : {}),
          });

          // Accepted by the gateway only; the confirmation poller settles it as sent or failed
          setQueueItems((prev) =>
            prev.map((it, idx) =>
              idx === i
                ? {
                    ...it,
                    status: 'queued',
                    logId: res?.data?.log_id ?? null,
                    queuedAt: Date.now(),
                    timestamp: new Date().toLocaleTimeString(),
                  }
                : it
            )
          );
        } catch (err) {
          setQueueItems((prev) =>
            prev.map((it, idx) =>
              idx === i
                ? {
                    ...it,
                    status: 'error',
                    error: err.message || 'Gagal mengirim pesan',
                    timestamp: new Date().toLocaleTimeString(),
                  }
                : it
            )
          );
        }

        // Wait interval with 10%–20% organic margin jitter if not the last item
        const isLastItem = i === items.length - 1;
        if (!isLastItem && !cancelRef.current) {
          const organicDelayMs = getOrganicInterval(intervalMs);
          setCountdown(Math.ceil(organicDelayMs / 1000));
          const continued = await waitWithControl(
            organicDelayMs,
            cancelRef,
            pauseRef,
            (sec) => setCountdown(sec)
          );
          setCountdown(0);
          if (!continued) break;
        }
      }

      setCountdown(0);
      setIsFinished(true);

      if (cancelRef.current) {
        setStatus('cancelled');
      } else {
        setStatus('completed');
      }
    })();
  }, []);

  // Format widget control actions based on current status
  const renderActions = () => {
    if (status === 'running') {
      return (
        <>
          <Button
            size="xs"
            variant="secondary"
            icon={Pause}
            onClick={pauseQueue}
          >
            Jeda
          </Button>
          <Button
            size="xs"
            variant="danger"
            icon={StopCircle}
            onClick={cancelQueue}
          >
            Batalkan
          </Button>
        </>
      );
    }

    if (status === 'paused') {
      return (
        <>
          <Button
            size="xs"
            variant="primary"
            icon={Play}
            onClick={resumeQueue}
          >
            Lanjutkan
          </Button>
          <Button
            size="xs"
            variant="danger"
            icon={StopCircle}
            onClick={cancelQueue}
          >
            Batalkan
          </Button>
        </>
      );
    }

    return (
      <Button
        size="xs"
        variant="secondary"
        icon={Check}
        onClick={dismissQueue}
      >
        Selesai & Tutup
      </Button>
    );
  };

  // Format items array for generic TaskProgressWidget
  const formattedItems = queueItems.map((it) => ({
    id: it.id,
    title: it.recipient.name,
    subtitle: `${it.product.item_name || 'Produk'} • ${it.tone}${
      it.status === 'queued' ? ' • Diteruskan, menunggu konfirmasi gateway' : ''
    }`,
    badge: it.recipient.isGroup ? 'Grup' : 'Kontak',
    badgeVariant: it.recipient.isGroup ? 'accent' : 'neutral',
    status: it.status,
    error: it.error,
  }));

  const widgetTitle =
    status === 'completed'
      ? 'Pengiriman Selesai'
      : status === 'cancelled'
        ? 'Pengiriman Dibatalkan'
        : status === 'paused'
          ? 'Pengiriman Dijeda'
          : 'Mengirim Pesan WhatsApp';

  const footerNotice =
    countdown > 0 && status === 'running'
      ? `Mengirim pesan berikutnya dalam ${countdown} detik...`
      : isFinished && queuedCount > 0
        ? `${queuedCount} pesan masih diproses WhatsApp Gateway. Status akhir tercatat di Riwayat Pesan.`
        : isFinished
          ? 'Dapat ditinjau sebelum ditutup.'
          : 'Proses berjalan di latar belakang.';

  return (
    <BroadcastQueueContext.Provider
      value={{
        isQueueActive,
        isMinimized,
        isPaused,
        isFinished,
        status,
        queueItems,
        currentIndex,
        totalItems: queueItems.length,
        successCount,
        failedCount,
        queuedCount,
        countdown,
        startQueue,
        pauseQueue,
        resumeQueue,
        cancelQueue,
        toggleMinimize,
        dismissQueue,
      }}
    >
      {children}

      {/* Persistent Floating Google Drive-Style Progress Widget */}
      <TaskProgressWidget
        isOpen={isQueueActive}
        title={widgetTitle}
        isMinimized={isMinimized}
        onToggleMinimize={toggleMinimize}
        onClose={status === 'running' || status === 'paused' ? cancelQueue : dismissQueue}
        status={status}
        progress={{
          current: currentIndex,
          total: queueItems.length,
        }}
        stats={[
          { label: 'Terkirim', value: successCount, variant: 'success' },
          { label: 'Menunggu', value: queuedCount, variant: queuedCount > 0 ? 'warning' : 'neutral' },
          { label: 'Gagal', value: failedCount, variant: failedCount > 0 ? 'danger' : 'neutral' },
        ]}
        items={formattedItems}
        actions={renderActions()}
        footerNotice={footerNotice}
      />
    </BroadcastQueueContext.Provider>
  );
}

/**
 * Hook to access broadcast background queue methods and state.
 */
export function useBroadcastQueue() {
  const context = useContext(BroadcastQueueContext);
  return context || null;
}

